import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Apple, Heart, Bell, BookOpen, ShieldCheck } from "lucide-react";
import { Section } from "./Common";
import BRAND from "../constants/brand";

/**
 * App Download Section — premium dual-pane layout with a luxury phone
 * mockup showcasing the DONiTSH!T mobile experience.  All "coming soon"
 * messaging removed per Phase 6.2 requirements.
 */
export default function AppDownloadSection() {
  const { t } = useTranslation();
  const featureLabels = t("app.features", { returnObjects: true }) || [];
  const featureIcons = [Sparkles, Heart, Bell, BookOpen, ShieldCheck];
  const features = (Array.isArray(featureLabels) ? featureLabels : []).map((label, idx) => ({
    Icon: featureIcons[idx] || Sparkles,
    label,
  }));

  return (
    <Section
      eyebrow={t("app.eyebrow")}
      title={t("app.title")}
      subtitle={t("app.subtitle")}
    >
      <div
        id="app-download-section"
        className="relative card-surface overflow-hidden p-6 md:p-10 lg:p-14 scroll-mt-28"
        data-testid="app-download-section"
      >
        {/* Subtle radial glow background */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full opacity-50 blur-3xl"
          style={{
            background:
              "radial-gradient(circle at center, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0.0) 65%)",
          }}
        />
        <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          {/* ---- Left: copy + feature list + store badges */}
          <div>
            <div className="eyebrow mb-3" data-testid="app-platforms-eyebrow">{t("app.platforms_eyebrow")}</div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-[22ch]">
              <span className="text-white">{t("app.h_pre")}</span>{" "}
              <span className="gold-text">{t("app.h_acc")}</span>
            </h3>
            <p className="mt-5 text-white/72 leading-relaxed body-copy max-w-[55ch] text-base md:text-lg">
              {t("app.body")}
            </p>

            {/* Feature bullets */}
            <ul className="mt-7 grid sm:grid-cols-2 gap-2.5 max-w-xl" data-testid="app-features">
              {features.map(({ Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 text-sm text-white/85 rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5"
                >
                  <span className="w-7 h-7 rounded-md bg-gradient-to-br from-[#E2C77A]/30 to-[#7A5F2A]/15 border border-[#C8A24A]/30 grid place-items-center shrink-0">
                    <Icon className="w-3.5 h-3.5 gold-text" />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            {/* Store badges */}
            <div className="mt-8 flex flex-wrap gap-3" data-testid="app-store-badges">
              <a
                href="https://apps.apple.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t("app.app_store_pre")} ${t("app.app_store")}`}
                data-testid="badge-app-store"
                className="group inline-flex items-center gap-3 rounded-xl border border-white/15
                           bg-black/85 px-4 py-3 hover:border-[#C8A24A]/50 hover:bg-black transition-all"
              >
                <Apple className="w-7 h-7 text-white" />
                <div className="leading-tight">
                  <div className="text-[10px] tracking-[0.12em] text-white/65 uppercase">{t("app.app_store_pre")}</div>
                  <div className="font-display text-base">{t("app.app_store")}</div>
                </div>
              </a>

              <a
                href="https://play.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t("app.google_play_pre")} ${t("app.google_play")}`}
                data-testid="badge-google-play"
                className="group inline-flex items-center gap-3 rounded-xl border border-white/15
                           bg-black/85 px-4 py-3 hover:border-[#C8A24A]/50 hover:bg-black transition-all"
              >
                <GooglePlayIcon />
                <div className="leading-tight">
                  <div className="text-[10px] tracking-[0.12em] text-white/65 uppercase">{t("app.google_play_pre")}</div>
                  <div className="font-display text-base">{t("app.google_play")}</div>
                </div>
              </a>
            </div>
          </div>

          {/* ---- Right: premium phone mockup */}
          <div className="relative mx-auto">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================ */
/* Phone Mockup — luxe iPhone-style frame with DONiTSH!T inside  */
/* ============================================================ */
function PhoneMockup() {
  return (
    <div
      className="relative mx-auto"
      style={{ width: 280 }}
      data-testid="phone-mockup"
    >
      {/* Glow halo */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -m-10 rounded-[60px] blur-2xl"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.0) 70%)",
        }}
      />

      {/* Outer chassis */}
      <div
        className="relative rounded-[48px] border border-[#2a2a35] bg-gradient-to-b from-[#1a1c25] to-[#0c0d13] p-2 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
        style={{ aspectRatio: "9 / 19" }}
      >
        {/* Side rail accents (gold) */}
        <span className="absolute top-24 -left-[3px] w-[3px] h-10 rounded-r-md bg-gradient-to-b from-[#E2C77A] to-[#7A5F2A]" />
        <span className="absolute top-44 -left-[3px] w-[3px] h-16 rounded-r-md bg-gradient-to-b from-[#E2C77A] to-[#7A5F2A]" />
        <span className="absolute top-36 -right-[3px] w-[3px] h-20 rounded-l-md bg-gradient-to-b from-[#E2C77A] to-[#7A5F2A]" />

        {/* Screen */}
        <div className="relative h-full w-full rounded-[40px] overflow-hidden bg-[#070708] border border-white/5">
          {/* Dynamic Island */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full bg-black z-10 border border-white/10" />

          {/* Screen content */}
          <PhoneScreen />
        </div>
      </div>
    </div>
  );
}

function PhoneScreen() {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Hero artwork header */}
      <div className="relative h-[42%] overflow-hidden">
        <img
          src={BRAND.HERO}
          alt="DONiTSH!T mobile hero"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-[#070708]" />
        <div className="absolute top-10 left-4 right-4">
          <div className="text-[8px] tracking-[0.28em] uppercase gold-text">{t("app.mockup.today_eyebrow")}</div>
          <div className="font-display text-lg leading-tight mt-1">
            {t("app.mockup.good_morning_pre")}<br /><span className="gold-text">{t("app.mockup.good_morning_acc")}</span>
          </div>
        </div>
      </div>

      {/* Card stack */}
      <div className="relative -mt-8 px-3 flex-1 space-y-2.5">
        {/* Dosha snapshot card */}
        <div className="rounded-2xl border border-[#C8A24A]/30 bg-[#0E1015]/95 backdrop-blur p-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]">
          <div className="text-[7px] tracking-[0.28em] uppercase gold-text">{t("app.mockup.your_dosha")}</div>
          <div className="font-display text-sm mt-1.5">{t("app.mockup.balanced_today")}</div>
          <div className="mt-2 grid grid-cols-3 gap-1">
            <span className="h-1 rounded-full bg-gradient-to-r from-[#E2C77A] to-[#D4AF37]" />
            <span className="h-1 rounded-full bg-white/15" />
            <span className="h-1 rounded-full bg-white/15" />
          </div>
        </div>

        {/* Today's ritual card */}
        <div className="rounded-2xl border border-white/10 bg-[#0E1015]/95 p-3">
          <div className="flex items-start gap-2.5">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center">
              <Sparkles className="w-3.5 h-3.5 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[7px] tracking-[0.28em] uppercase gold-text">{t("app.mockup.today_ritual")}</div>
              <div className="text-[11px] mt-0.5 text-white/85 leading-snug">
                {t("app.mockup.ritual_text")}
              </div>
            </div>
          </div>
        </div>

        {/* Ask Donny micro chip */}
        <div className="rounded-full border border-[#C8A24A]/35 bg-[#15161D] px-3 py-1.5 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-[#C8A24A]/60">
            <img src={BRAND.DONNY_AVATAR} alt="" className="w-full h-full object-cover" />
          </span>
          <span className="text-[10px] text-white/85 truncate">{t("app.mockup.ask_donny_chip")}</span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="relative px-3 pb-3 pt-2.5">
        <div className="rounded-2xl border border-white/10 bg-[#0E1015]/95 px-3 py-2 flex items-center justify-between">
          {[
            t("app.mockup.tab_home"),
            t("app.mockup.tab_hub"),
            t("app.mockup.tab_donny"),
            t("app.mockup.tab_shop"),
            t("app.mockup.tab_me"),
          ].map((tab, i) => (
            <div key={tab} className="flex flex-col items-center gap-0.5">
              <span
                className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-[#D4AF37]" : "bg-white/30"}`}
              />
              <span className={`text-[8px] ${i === 0 ? "text-white" : "text-white/55"}`}>{tab}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GooglePlayIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="gp1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D2FF" />
          <stop offset="100%" stopColor="#3A7BD5" />
        </linearGradient>
        <linearGradient id="gp2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFD200" />
          <stop offset="100%" stopColor="#F7971E" />
        </linearGradient>
        <linearGradient id="gp3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF416C" />
          <stop offset="100%" stopColor="#FF4B2B" />
        </linearGradient>
        <linearGradient id="gp4" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#43E97B" />
          <stop offset="100%" stopColor="#38F9D7" />
        </linearGradient>
      </defs>
      <path d="M85 32 L300 247 85 462 Z" fill="url(#gp1)" />
      <path d="M85 32 L300 247 380 167 165 32 Z" fill="url(#gp4)" />
      <path d="M85 462 L300 247 380 327 165 462 Z" fill="url(#gp3)" />
      <path d="M380 167 L460 247 380 327 300 247 Z" fill="url(#gp2)" />
    </svg>
  );
}
